import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  #ecsLocked = false

  static targets = [
    'accordion',
    'accordionButton',
    'accordionLabel',
    'toggle',
    'toggleWrapper',
    'gestureCheckbox'
  ]

  static values = {
    partial: Boolean,
    seeMoreText: String,
    seeLessText: String
  }

  connect () {
    this.isOpen = false
    this.#syncToggleFromCheckboxes()
    this.#syncEcsLock()
  }

  toggleAccordion () {
    this.isOpen = !this.isOpen
    this.accordionTarget.hidden = !this.isOpen

    if (this.isOpen) {
      this.accordionButtonTarget.setAttribute('data-open', '')
    } else {
      this.accordionButtonTarget.removeAttribute('data-open')
    }

    if (this.hasAccordionLabelTarget) {
      this.accordionLabelTarget.textContent = this.isOpen
        ? this.seeLessTextValue
        : this.seeMoreTextValue
    }
  }

  /**
   * Handles the toggle click with a 3-state machine:
   *   partial (gray)  → whole family (team-primary, auto-check gestures)
   *   whole family    → OFF
   *   OFF             → whole family (auto-check gestures)
   *
   * Accordion rules:
   *   - open + any transition  → keep open
   *   - closed + toggle OFF    → open
   *   - closed + toggle ON     → keep closed
   */
  toggleClicked (event) {
    event.preventDefault()

    const toggle = this.toggleTarget
    const wasOpen = this.isOpen
    const wasChecked = toggle.checked

    if (this.partialValue) {
      toggle.checked = true
      this.#checkAllGestures()
    } else if (toggle.checked) {
      toggle.checked = false
      this.#clearGestureCheckboxes()
      this.#setToggleColor('inactive')
      this.#syncEcsLock()
    } else {
      toggle.checked = true
      this.#checkAllGestures()
    }

    if (!wasOpen && !wasChecked) {
      this.#openAccordion()
    }
  }

  gestureChanged () {
    this.#syncToggleFromCheckboxes()
    this.#syncEcsLock()
  }

  #openAccordion () {
    if (!this.hasAccordionTarget) return

    this.accordionTarget.hidden = false
    this.accordionButtonTarget.setAttribute('data-open', '')

    if (this.hasAccordionLabelTarget) {
      this.accordionLabelTarget.textContent = this.seeLessTextValue
    }

    this.isOpen = true
  }

  #closeAccordion () {
    if (!this.hasAccordionTarget) return

    this.accordionTarget.hidden = true
    this.accordionButtonTarget.removeAttribute('data-open')

    if (this.hasAccordionLabelTarget) {
      this.accordionLabelTarget.textContent = this.seeMoreTextValue
    }

    this.isOpen = false
  }

  #clearGestureCheckboxes () {
    this.gestureCheckboxTargets.forEach(cb => { cb.checked = false })
  }

  #checkAllGestures () {
    const ecsOn = this.#isEcsOn()
    this.gestureCheckboxTargets.forEach(cb => {
      const isDoubleService = cb.dataset.doubleService === 'true'
      cb.checked = isDoubleService ? ecsOn : true
    })
    this.#syncToggleFromCheckboxes()
    this.#syncEcsLock()
    if (this.toggleTarget.checked) {
      this.partialValue = false
      const allChecked = this.gestureCheckboxTargets.every(cb => cb.checked)
      this.#setToggleColor(allChecked ? 'active' : 'partial')
    }
  }

  #syncToggleFromCheckboxes () {
    const checkboxes = this.gestureCheckboxTargets
    if (checkboxes.length === 0) return

    const checkedCount = checkboxes.filter(cb => cb.checked).length

    if (checkedCount === 0) {
      this.toggleTarget.checked = false
      this.#setToggleColor('inactive')
      this.partialValue = false
    } else if (checkedCount === checkboxes.length) {
      this.toggleTarget.checked = true
      this.#setToggleColor('active')
      this.partialValue = false
    } else {
      this.toggleTarget.checked = true
      this.#setToggleColor('partial')
      this.partialValue = true
    }
  }

  #setToggleColor (state) {
    const wrapper = this.toggleWrapperTarget
    if (state === 'active') {
      wrapper.style.backgroundColor = 'var(--color-team-primary)'
    } else if (state === 'partial') {
      wrapper.style.backgroundColor = 'color-mix(in srgb, var(--color-team-primary) 40%, white)'
    } else {
      wrapper.style.backgroundColor = ''
    }
  }

  #isEcsOn () {
    const ecsRow = this.element.closest('[data-scenario-cards-target="card"]')
      ?.querySelector('[data-family="hot_water"]')
    return ecsRow?.querySelector('[data-scenario-family-target="toggle"]')?.checked ?? false
  }

  #hasCheckedDoubleServiceGestures () {
    return this.gestureCheckboxTargets.some(
      cb => cb.dataset.doubleService === 'true' && cb.checked
    )
  }

  #syncEcsLock () {
    if (this.element.dataset.family !== 'heating') return

    const ecsRow = this.element.closest('[data-scenario-cards-target="card"]')
      ?.querySelector('[data-family="hot_water"]')
    if (!ecsRow) return

    if (this.#hasCheckedDoubleServiceGestures()) {
      if (!this.#ecsLocked) {
        this.#lockEcs(ecsRow)
        this.#ecsLocked = true
      }
    } else if (this.#ecsLocked) {
      this.#unlockEcs(ecsRow)
      this.#ecsLocked = false
    }
  }

  #lockEcs (ecsRow) {
    const toggle = ecsRow.querySelector('[data-scenario-family-target="toggle"]')
    const wrapper = ecsRow.querySelector('[data-scenario-family-target="toggleWrapper"]')
    const accordion = ecsRow.querySelector('[data-scenario-family-target="accordion"]')
    toggle.checked = true
    wrapper.style.backgroundColor = 'var(--color-team-primary)'
    wrapper.style.pointerEvents = 'none'
    if (accordion) accordion.style.pointerEvents = 'none'
    ecsRow.querySelectorAll('[data-scenario-family-target="gestureCheckbox"]')
      .forEach(cb => { cb.checked = true })
  }

  #unlockEcs (ecsRow) {
    const toggle = ecsRow.querySelector('[data-scenario-family-target="toggle"]')
    const wrapper = ecsRow.querySelector('[data-scenario-family-target="toggleWrapper"]')
    const accordion = ecsRow.querySelector('[data-scenario-family-target="accordion"]')
    toggle.checked = false
    wrapper.style.backgroundColor = ''
    wrapper.style.pointerEvents = ''
    if (accordion) accordion.style.pointerEvents = ''
    ecsRow.querySelectorAll('[data-scenario-family-target="gestureCheckbox"]')
      .forEach(cb => { cb.checked = false })
  }
}
